import { APP_PERMISSIONS } from '@/shared/constants/app-permissions';
import { APP_MENU_MODEL } from './app-menu-model';
import { hasAnyAuthority, pageAuthorities } from './has-any-authority';
function flattenMenuRoutes(items) {
    const routes = [];
    for (const item of items) {
        if (item.items?.length) {
            routes.push(...flattenMenuRoutes(item.items));
            continue;
        }
        if (item.to) {
            routes.push({ path: item.to, authorities: item.authorities });
        }
    }
    return routes;
}
/** Menu routes in sidebar order — used for default landing page selection. */
export const APP_MENU_ROUTE_ENTRIES = flattenMenuRoutes(APP_MENU_MODEL);
/** Authenticated routes not listed in the sidebar (nested / utility pages). */
const SUPPLEMENTAL_ROUTE_ENTRIES = [
    {
        path: '/crm/agent-panel/tickets',
        authorities: pageAuthorities(APP_PERMISSIONS.CASEMANAGEMENT_AGENTPANEL),
    },
    {
        path: '/campaigns/campaign',
        authorities: pageAuthorities(APP_PERMISSIONS.CAMPAIGNS_LIST, APP_PERMISSIONS.CAMPAIGNS_DASHBOARD, APP_PERMISSIONS.CAMPAIGN_READ),
    },
    {
        path: '/wallboard',
        authorities: pageAuthorities(APP_PERMISSIONS.CALL_CENTER_DASHBOARD),
    },
    {
        path: '/qstat/voicemail',
        authorities: pageAuthorities(APP_PERMISSIONS.CALL_CENTER_VOICEMAIL, APP_PERMISSIONS.VOICEMAIL_READ),
    },
];
const ROUTE_ACCESS_ENTRIES = [...APP_MENU_ROUTE_ENTRIES, ...SUPPLEMENTAL_ROUTE_ENTRIES];
function normalizePath(path) {
    const trimmed = path.split('?')[0]?.split('#')[0]?.trim() ?? '/';
    if (!trimmed || trimmed === '/')
        return '/';
    return trimmed.endsWith('/') && trimmed.length > 1 ? trimmed.slice(0, -1) : trimmed;
}
function sessionCanAccessRoute(session, entry) {
    if (!session)
        return false;
    if (!entry.authorities?.length)
        return true;
    return hasAnyAuthority(session.permissions, entry.authorities);
}
function findRouteEntryForPath(path) {
    const normalized = normalizePath(path);
    const exact = ROUTE_ACCESS_ENTRIES.find((entry) => entry.path === normalized);
    if (exact)
        return exact;
    return ROUTE_ACCESS_ENTRIES
        .filter((entry) => entry.path !== '/' &&
        (normalized === entry.path || normalized.startsWith(`${entry.path}/`)))
        .sort((a, b) => b.path.length - a.path.length)[0];
}
/**
 * First landing page after login (or when a guest hits `/` while already signed in).
 * Walks the sidebar menu in order and picks the first route the user may access.
 */
export function resolveDefaultHomePath(session) {
    if (!session) {
        return '/login';
    }
    for (const entry of APP_MENU_ROUTE_ENTRIES) {
        if (sessionCanAccessRoute(session, entry)) {
            return entry.path;
        }
    }
    return '/unauthorized';
}
/** Whether the session may open this path (exact or nested under a known route). */
export function canAccessPath(session, path) {
    if (!session)
        return false;
    const entry = findRouteEntryForPath(path);
    if (!entry)
        return false;
    return sessionCanAccessRoute(session, entry);
}
const AUTH_ONLY_PATHS = new Set(['/login', '/auth/login', '/unauthorized', '/no-role-assigned']);
/**
 * Post-login redirect: honor a saved deep link only when permitted; otherwise first allowed menu route.
 */
export function resolvePostLoginPath(session, savedFrom) {
    const fallback = resolveDefaultHomePath(session);
    const from = savedFrom?.trim();
    if (!from || AUTH_ONLY_PATHS.has(normalizePath(from))) {
        return fallback;
    }
    return canAccessPath(session, from) ? normalizePath(from) : fallback;
}
